import { classify_api_failure, failure_message, type ApiFailure } from './api-failure'
import { escape_html } from './html'
import { icon } from './icons'

/**
 * Carregar coleção sem transformar falha em lista vazia.
 *
 * O padrão que isto substitui — `.catch(() => ({ items: [], total: 0 }))` —
 * fazia a fila de moderação dizer "nenhum anúncio aguardando" quando a API
 * estava fora. Indistinguível de fila limpa: o moderador fecha a tela achando
 * que não há trabalho.
 *
 * O banner é **irmão** do container repintado, nunca filho: quem pinta a lista
 * troca o `innerHTML` do próprio container, e um banner lá dentro sumiria no
 * primeiro repinte — inclusive no repinte que acontece logo depois do erro.
 */

export type LoadOutcome<T> = { ok: true; data: T } | { ok: false; failure: ApiFailure }

export async function carregar_colecao<T>(rotulo: string, fn: () => Promise<T>): Promise<LoadOutcome<T>> {
  try {
    return { ok: true, data: await fn() }
  } catch (err) {
    const failure = classify_api_failure(err)
    // Falha continua diagnosticável no console — mas nunca SÓ no console.
    console.error(`[carregar] ${rotulo}:`, failure.kind, failure.status ?? '-', failure.code)
    return { ok: false, failure }
  }
}

/**
 * Padrão de alerta do `docs/ui-padrao.md`: faixa lateral colorida + fundo neutro
 * + texto ink, com badge de tom sólido. Passa AA em light e dark por construção.
 */
export function failure_banner_html(rotulo: string, failure: ApiFailure): string {
  // Classes LITERAIS, nunca `bg-${tom}`: o Tailwind gera CSS a partir do que
  // encontra escrito no fonte. Classe montada por interpolação não existe no
  // bundle — o banner sairia sem cor nenhuma, e só em produção.
  const negado = failure.kind === 'forbidden'
  const faixa = negado ? 'border-l-warning' : 'border-l-danger'
  const badge = negado ? 'bg-warning text-warning-ink' : 'bg-danger text-danger-ink'
  const titulo = negado ? 'Sem permissão' : 'Não foi possível carregar'

  return `<div class="mb-4 rounded-md border-l-4 ${faixa} bg-surface2 p-3" role="alert" data-failure-banner>
    <div class="flex flex-wrap items-center gap-2">
      <span class="rounded ${badge} px-2 py-0.5 text-[13px] font-bold uppercase tracking-wide">${escape_html(titulo)}</span>
      <span class="text-sm text-ink">${escape_html(rotulo)}</span>
    </div>
    <p class="mt-2 text-sm text-ink">${escape_html(failure_message(failure))}</p>
    ${
      failure.retryable
        ? `<button type="button" data-failure-retry class="mt-3 inline-flex h-9 items-center gap-2 rounded-md border border-border bg-surface px-3 text-xs font-bold uppercase tracking-wide text-ink hover:bg-surface2">${icon('refresh', 'h-4 w-4')} Recarregar</button>`
        : ''
    }
  </div>`
}

/** Liga o botão Recarregar do banner que acabou de ser pintado. */
export function bind_failure_banner(el: HTMLElement, on_retry: () => void): void {
  el.querySelector<HTMLButtonElement>('[data-failure-retry]')?.addEventListener('click', () => {
    on_retry()
  })
}

/**
 * Pinta (ou limpa) o banner num slot dedicado. Passar `null` limpa — é o que
 * faz o banner sumir quando o carregamento seguinte dá certo.
 */
export function render_failure(
  slot: HTMLElement | null,
  rotulo: string,
  failure: ApiFailure | null,
  on_retry?: () => void
): void {
  if (!slot) return
  if (!failure) {
    slot.innerHTML = ''
    return
  }
  slot.innerHTML = failure_banner_html(rotulo, failure)
  if (on_retry) bind_failure_banner(slot, on_retry)
}

/**
 * Bloco pronto para telas que repintam a página inteira: o banner ENTRA no lugar
 * da lista, nunca junto dela. Se a lista continuasse renderizada, o estado vazio
 * ("nenhum registro") apareceria embaixo do erro — e é exatamente essa leitura
 * ambígua que a entrega existe para acabar.
 */
export function failure_section_html(rotulo: string, failure: ApiFailure): string {
  return `<div data-failure-slot>${failure_banner_html(rotulo, failure)}</div>`
}

export function bind_failure_section(el: HTMLElement, on_retry: () => void): void {
  const slot = el.querySelector<HTMLElement>('[data-failure-slot]')
  if (slot) bind_failure_banner(slot, on_retry)
}
